import { useSeekaConverge } from "@seeka-labs/converge-react";
import { Box, Flex, Text, LegacyText, SelectOption } from "flicket-ui";
import { Select } from "~components/common/Select";
import {
  alcoholFreeZoneOptions,
  coverTypeOptions,
  seatSelectTypeOptions,
  sortOptions,
} from "~components/reservation/constants";
import {
  OptionKey,
  useSeatedReservationStore,
} from "~features/seated-reservation/store";

import * as actions from "~features/seated-reservation/store/actions/index";
import { useFilterOptions } from "~hooks/useFilterOptions";

interface FiltersProps {
  amountDisabled: boolean;
  seatAmountOptions: SelectOption[];
  filterOptions?: string[];
}

export const Filters = ({
  amountDisabled,
  seatAmountOptions,
  filterOptions,
}: FiltersProps) => {
  const options = useSeatedReservationStore((state) => state.options);
  const converge = useSeekaConverge();

  const updateOptions = async (key: OptionKey, value: any) => {
    await actions.updateOptions({ options: { [key]: value } }, converge);
  };

  const { hasOption } = useFilterOptions(filterOptions);

  return (
    <Box
      bg="N200"
      d={{ _: "none", md: "flex" }}
      py="6/4"
      px={4}
      w="100%"
      alignItems="center"
      justifyContent="space-between"
    >
      <Flex alignItems="center">
        <LegacyText
          color="N800"
          fontWeight="demiBold"
          mr={2}
          whiteSpace="nowrap"
          fontSize={2}
        >
          I need tickets for
        </LegacyText>
        <Box minW="124px" mr={2}>
          <Select
            value={seatAmountOptions.find(
              (o) => o.value === String(options.amount)
            )}
            options={seatAmountOptions}
            onChange={(value) => void updateOptions("amount", Number(value))}
            isDisabled={amountDisabled}
            size="compact"
          />
        </Box>

        {hasOption("cover") && (
          <>
            <LegacyText
              fontSize={2}
              color="N800"
              mr={1}
              whiteSpace="nowrap"
              fontWeight="demiBold"
            >
              Seat cover
            </LegacyText>
            <Box minW="124px" mr={2}>
              <Select
                value={coverTypeOptions.find(
                  (opt) => opt.value === options.coverType
                )}
                options={coverTypeOptions}
                onChange={(value) => {
                  void updateOptions("coverType", value);
                }}
                hideSelectedOptions
                size="compact"
                selectProps={{
                  isSearchable: false,
                }}
              />
            </Box>
          </>
        )}

        {hasOption("alcohol") && (
          <>
            <LegacyText
              fontSize={2}
              color="N800"
              mr={1}
              whiteSpace="nowrap"
              fontWeight="demiBold"
            >
              Alcohol free zone
            </LegacyText>
            <Box minW="124px" mr={2}>
              <Select
                value={alcoholFreeZoneOptions.find(
                  (option) => option.value === options.zoneType
                )}
                options={alcoholFreeZoneOptions}
                onChange={(value) => {
                  void updateOptions("zoneType", value);
                }}
                size="compact"
              />
            </Box>
          </>
        )}

        <Box pl={2} borderLeft="1px solid" borderColor="N300">
          <LegacyText
            fontSize={2}
            color="N800"
            whiteSpace="nowrap"
            mr={1}
            fontWeight="demiBold"
          >
            Sort by
          </LegacyText>
        </Box>
        <Box minW="124px">
          <Select
            value={sortOptions.find(
              (opt) => opt.value === options.selectedSort
            )}
            options={sortOptions}
            onChange={(value) => {
              void updateOptions("selectedSort", value);
            }}
            mr={2}
            size="compact"
            menuProps={{
              showSelectedTick: false,
            }}
          />
        </Box>
      </Flex>
      <Flex alignItems="center">
        <Box pl={2} borderColor="N300">
          <LegacyText
            fontSize={2}
            color="N800"
            whiteSpace="nowrap"
            mr={1}
            fontWeight="demiBold"
          >
            Seat selection
          </LegacyText>
        </Box>
        <Box minW="124px">
          <Select
            value={seatSelectTypeOptions.find(
              (opt) => opt.value === options.seatSelectType
            )}
            options={seatSelectTypeOptions}
            onChange={(value) => {
              void updateOptions("seatSelectType", value);
            }}
            hideSelectedOptions
            size="compact"
            mr={2}
            selectProps={{
              isSearchable: false,
            }}
          />
        </Box>
      </Flex>
    </Box>
  );
};
